import { useState } from 'react';
import { css } from '@emotion/react';
import { capitalize } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, SwitchChipGroup, Text } from '@frontend/design-system';
import { insuranceBenefitSummaryAmountStyle } from '../styles';
import { BenefitLimitOverview } from '../utils/types';
import { getSwitchChipSelectedClassName, hasInNetworkData, hasOutNetworkData, toTitleCase } from '../utils/utils';
import { DetailText } from './detail-text';

interface Props {
  benefitDetails: BenefitLimitOverview | undefined;
  handleOpenInsuranceReport: () => void;
}

type NetworkType = 'in-network' | 'out-network';

export const InsuranceBenefitSummary = ({ benefitDetails, handleOpenInsuranceReport }: Props) => {
  const { t } = useTranslation();
  const defaultNetworkType = hasInNetworkData(benefitDetails) ? 'in-network' : 'out-network';
  const [networkType, setNetworkType] = useState<NetworkType>(defaultNetworkType);
  if (!benefitDetails) {
    return null;
  }
  const { maximum, deductible } = benefitDetails;
  const { inNetwork: inNetworkMaximum, outOfNetwork: outOfNetworkMaximum } = maximum;
  const { inNetwork: inNetworkDeductible, outOfNetwork: outOfNetworkDeductible } = deductible;

  const isInNetwork = networkType === 'in-network';
  const selectedNetworkDetailsDeductible = isInNetwork ? inNetworkDeductible : outOfNetworkDeductible;
  const selectedNetworkDetailsMaximum = isInNetwork ? inNetworkMaximum : outOfNetworkMaximum;

  return (
    <>
      <section
        css={{ padding: theme.spacing(2, 3, 1), display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}
      >
        <div
          css={{
            marginBottom: theme.spacing(1),
          }}
        >
          <SwitchChipGroup
            css={switchChip}
            singleSelect
            defaultValue={defaultNetworkType}
            setValue={(val) => {
              setNetworkType(val.toString() as NetworkType);
            }}
            value={[networkType]}
          >
            {hasInNetworkData(benefitDetails) && (
              <SwitchChipGroup.Option
                className={getSwitchChipSelectedClassName(networkType, 'in-network')}
                value='in-network'
              >
                {t('In-Network')}
              </SwitchChipGroup.Option>
            )}
            {hasOutNetworkData(benefitDetails) && (
              <SwitchChipGroup.Option
                className={getSwitchChipSelectedClassName(networkType, 'out-network')}
                value='out-network'
              >
                {t('Out-Network')}
              </SwitchChipGroup.Option>
            )}
          </SwitchChipGroup>
        </div>
        <div css={insuranceBenefitSummaryAmountStyle}>
          {selectedNetworkDetailsMaximum && (
            <>
              <Heading level={3} css={{ marginBottom: theme.spacing(1) }}>
                {t('Maximum')}
              </Heading>
              <dl css={listStyle}>
                <div>
                  <Text as='dt'>{t('Vertical')}</Text>
                  <DetailText value={toTitleCase(selectedNetworkDetailsMaximum?.category)} />
                </div>
                <div>
                  <Text as='dt'>{t('Remaining/Max')}</Text>
                  <p className='total-amount'>
                    <span className='spent-amount'>
                      {'$' + Math.floor((selectedNetworkDetailsMaximum.remaining || 0) / 100)}
                    </span>
                    {`/$${Math.floor((selectedNetworkDetailsMaximum.amount || 0) / 100)}`}
                  </p>
                </div>
                <div>
                  <Text as='dt'>{t('Network Name')}</Text>
                  <DetailText value={toTitleCase(selectedNetworkDetailsMaximum?.networkName, ' ')} required />
                </div>
                <div>
                  <Text as='dt'>{t('Coverage Level')}</Text>
                  <DetailText value={capitalize(selectedNetworkDetailsMaximum?.coverageLevel)} required />
                </div>
              </dl>
            </>
          )}
          {selectedNetworkDetailsDeductible && (
            <>
              <Heading level={3} css={{ marginBottom: theme.spacing(1) }}>
                {t('Deductible')}
              </Heading>
              <dl css={listStyle}>
                <div>
                  <Text as='dt'>{t('Vertical')}</Text>
                  <DetailText value={toTitleCase(selectedNetworkDetailsDeductible?.category)} required />
                </div>
                <div>
                  <Text as='dt'>{t('Remaining/Max Amount')}</Text>
                  <p className='total-amount'>
                    <span className='spent-amount'>
                      {'$' + Math.floor((selectedNetworkDetailsDeductible.remaining || 0) / 100)}
                    </span>
                    {`/$${Math.floor((selectedNetworkDetailsDeductible.amount || 0) / 100)}`}
                  </p>
                </div>
                <div>
                  <Text as='dt'>{t('Network Name')}</Text>
                  <DetailText value={toTitleCase(selectedNetworkDetailsDeductible?.networkName, ' ')} required />
                </div>
                <div>
                  <Text as='dt'>{t('Coverage Level')}</Text>
                  <DetailText value={capitalize(selectedNetworkDetailsDeductible?.coverageLevel)} required />
                </div>
              </dl>
            </>
          )}
        </div>
        <Button
          variant='tertiary'
          css={css({
            width: '100%',
          })}
          onClick={handleOpenInsuranceReport}
        >
          {t('Show More')}
        </Button>
      </section>
    </>
  );
};

const listStyle = css`
  margin-bottom: ${theme.spacing(2)};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing(1)};
  grid-auto-flow: dense;
  > div {
    display: flex;
    flex-direction: column;

    dt {
      margin-right: ${theme.spacing(4)};
      font-size: ${theme.fontSize(12)};
      margin-bottom: ${theme.spacing(0.5)};
      color: ${theme.font.colors.subdued};
    }
  }
`;
InsuranceBenefitSummary.displayName = 'InsuranceBenefitSummary';

const switchChip = css`
  gap: ${theme.spacing(2)};

  span {
    color: ${theme.colors.neutral90};
  }
  .selected,
  .selected:hover {
    background: ${theme.colors.primary5};
    border: solid 1px ${theme.colors.primary50};
  }
  .chip {
    height: ${theme.spacing(3)} !important;
    &:focus {
      outline: none;
    }
  }
`;
